import {
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Portal,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GoPlus } from 'react-icons/go'
import { IoCloseOutline, IoSettingsOutline } from 'react-icons/io5'
import { TbLink } from 'react-icons/tb'
import styled from 'styled-components'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import { isRestrictedUser, isViewOnlyUser } from '../../../utils/roles'
import AddBookmarkModal from '../../components/Bookmark/AddBookmarkModal'
import ManageBookmarkModal from '../../components/Bookmark/ManageBookmarkModal'
import SearchBar from '../../components/SearchBar'
import useToast from '../../hooks/useToast'
import {
  CreateBookmarkSuccessResponse,
  ListBookmarkSuccessResponse,
} from '../../interfaces/BookmarksResponse'
import { NavBarContext, UserContext } from '../../providers'
import PermissionGuard from '../PermissionGuard'

interface BookmarksDrawerProps {
  isNavbarOpen?: boolean
  isOpenBookmarks: boolean
  onCloseBookmarks: () => void
}

const ExtendedMenuHeading = styled.span`
  text-transform: uppercase;
  font-size: 12px;
  height: 18px;
  color: ${colors.appSlate};
`

const ExtendedMenuItem: FC<
  CreateBookmarkSuccessResponse & { getAndSetBookmarks: () => void }
> = ({
  bookmarkId,
  name,
  link,
  createdBy,
  updatedAt,
  createdAt,
  getAndSetBookmarks,
}) => {
    const { t } = useTranslation()
    const { user } = useContext(UserContext)
    const [isHovered, setIsHovered] = useState<boolean>(false)
    const {
      isOpen: isOpenManageBookmarkModal,
      onOpen: onOpenManageBookmarkModal,
      onClose: onCloseManageBookmarkModal,
    } = useDisclosure()

    const truncatedLink =
      link && link.length > 250 ? link.slice(0, 250) + '...' : link

    return (
      <>
        <Flex
          key={bookmarkId}
          color="appSlate"
          fontWeight="500"
          borderRadius="6px"
          width="full"
          p="8px 8px 8px 4px"
          align="center"
          cursor="pointer"
          _hover={{ bg: 'appPaleGreyThree' }}
          onClick={() => window.open(link)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Icon
            as={TbLink}
            color={colors.appSlate}
            height="18px"
            minWidth="14px"
            mr="8px"
          />
          <Tooltip label={truncatedLink} hasArrow>
            <Text
              className="ik-bookmark-name"
              color={isHovered ? 'appBlue' : 'appSlate'}
              isTruncated
              align="center"
              maxW="75%"
            >
              {name}
            </Text>
          </Tooltip>
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) => {
              const shouldShow =
                isHovered &&
                (!isRestrictedUser(user?.roles) || user?.id === createdBy.userId)
              return hasPermission && shouldShow ? (
                <Tooltip label={t('manage_bookmark', 'Manage bookmark')} hasArrow>
                  <IconButton
                    ml="auto"
                    aria-label={t('manage_bookmark', 'Manage bookmark')}
                    icon={<IoSettingsOutline />}
                    variant="link"
                    minWidth="16px"
                    borderRadius="4px"
                    height="16px"
                    color="appSlate"
                    onClick={e => {
                      e.stopPropagation()
                      onOpenManageBookmarkModal()
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )
            }}
          </PermissionGuard>
        </Flex>
        {isOpenManageBookmarkModal && (
          <ManageBookmarkModal
            name={name}
            link={link}
            bookmarkId={bookmarkId}
            isOpen={isOpenManageBookmarkModal}
            onClose={onCloseManageBookmarkModal}
            getAndSetBookmarks={getAndSetBookmarks}
          />
        )}
      </>
    )
  }

const BookmarksDrawer: FC<BookmarksDrawerProps> = ({
  isNavbarOpen,
  isOpenBookmarks,
  onCloseBookmarks,
}) => {
  const { t } = useTranslation()
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const { bookmarks, setBookmarks } = useContext(NavBarContext)
  const {
    isOpen: isOpenAddBookmarkModal,
    onOpen: onOpenAddBookmarkModal,
    onClose: onCloseAddBookmarkModal,
  } = useDisclosure()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { user } = useContext(UserContext)

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  const getAndSetBookmarks = async () => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/bookmarks`,
    )) as ListBookmarkSuccessResponse

    if (request.error) {
      toast({
        description: t(
          'error_in_getting_bookmarks',
          'Error in getting bookmarks, please try again later.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (Array.isArray(res)) {
      setBookmarks(res)
    }
    setIsInitialLoading(false)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseBookmarks()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    getAndSetBookmarks()
  }, [])

  const filteredBookmarks = useMemo(() => {
    if (!searchQuery) return bookmarks
    const query = searchQuery.toLowerCase()
    return bookmarks.filter(
      bookmark =>
        bookmark.name.toLowerCase().includes(query) ||
        bookmark.link.toLowerCase().includes(query),
    )
  }, [bookmarks, searchQuery])

  return (
    <Portal>
      <Flex
        direction="column"
        position="absolute"
        top="0"
        bottom="0"
        left={
          !window.isMediaLibraryWidget ? (isNavbarOpen ? '260px' : '72px') : '0'
        }
        width="312px"
        height="100vh"
        backgroundColor="#fbfcff"
        borderLeft="solid 1px #e2e8f0"
        borderRight="solid 1px #e2e8f0"
        boxShadow="-2px 0 18px 0 rgba(0, 0, 0, 0.16)"
        zIndex="98"
        px="16px"
        py="14px"
        transition="left var(--sidebarNavTransitionDuration) ease-in-out"
      >
        <Flex justify="space-between" align="center" mb="18px">
          <ExtendedMenuHeading>
            {t('bookmarks', 'Bookmarks')}
          </ExtendedMenuHeading>
          <IconButton
            aria-label={t('close', 'Close')}
            icon={<IoCloseOutline />}
            color="appBlueyGrey"
            variant="link"
            minWidth="18px"
            borderRadius="4px"
            height="18px"
            onClick={onCloseBookmarks}
            _hover={{ background: colors.appGrey }}
          />
        </Flex>

        <Flex align="center">
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder={t('bookmark_search_placeholder', 'Find a bookmark')}
            minWidth={isViewOnlyUser(user?.roles) ? '100%' : '85%'}
            showSearchIcon
          />
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission ? (
                <Tooltip label={t('add_bookmark', 'Add bookmark')} hasArrow>
                  <IconButton
                    ml="auto"
                    aria-label={t('add_bookmark', 'Add bookmark')}
                    icon={<GoPlus />}
                    color="appClearBlue"
                    backgroundColor="appPaleGrey"
                    variant="link"
                    minWidth="24px"
                    borderRadius="full"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="appGrey"
                    height="24px"
                    _hover={{ background: colors.appPaleGreyTwo }}
                    onClick={onOpenAddBookmarkModal}
                  />
                </Tooltip>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </Flex>

        <Divider my="12px" color="appPaleBlue" />

        <Box
          height="100%"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            scrollbarWidth: 'none',
          }}
        >
          {isInitialLoading ? (
            <>
              <Skeleton
                height="34px"
                width="full"
                borderRadius="6px"
                mb="8px"
              />
              <Skeleton
                height="34px"
                width="full"
                borderRadius="6px"
                mb="8px"
              />
              <Skeleton height="34px" width="full" borderRadius="6px" />
            </>
          ) : bookmarks.length === 0 ? (
            <Flex
              height="100%"
              align="center"
              justify="center"
              color="appSlate"
            >
              <Text>{t('no_bookmarks_saved', 'No bookmarks saved')}</Text>
            </Flex>
          ) : filteredBookmarks.length === 0 ? (
            <Flex
              height="100%"
              align="center"
              justify="center"
              color="appSlate"
            >
              <Text>{t('no_matches_found', 'No matches found')}</Text>
            </Flex>
          ) : (
            filteredBookmarks.map(bookmark => (
              <ExtendedMenuItem
                {...bookmark}
                getAndSetBookmarks={getAndSetBookmarks}
              />
            ))
          )}
        </Box>
      </Flex>
      {isOpenAddBookmarkModal && (
        <AddBookmarkModal
          isOpen={isOpenAddBookmarkModal}
          onClose={onCloseAddBookmarkModal}
          getAndSetBookmarks={getAndSetBookmarks}
        />
      )}
    </Portal>
  )
}

export default BookmarksDrawer
